import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  getCustomerTypes,
  getAllCustomers,
  getSalesExecutives,
} from '../services/customer_service'
import {
  getOldPayments,
  createOldPayment,
  updateOldPayment,
  deleteOldPayment,
} from '../services/old_payment_service'
import { useAuth } from '../context/AuthContext'
import './OldPayment.css'

const orderTypes = [
  { value: 'published_book', label: 'Publish Books' },
  { value: 'exercise_book', label: 'Exercise Books' },
]
const paymentMethods = [
  { value: 'Cash', label: 'Cash' },
  { value: 'Mobile Money', label: 'Mobile Money' },
  { value: 'Cheque', label: 'Cheque' },
  { value: 'Bank Transfer', label: 'Bank Transfer' },
]

const emptyFilters = {
  fromDate: '',
  toDate: '',
  fromAmount: '',
  toAmount: '',
  customer: '',
  customerType: '',
  orderType: '',
  salesExecutive: '',
  paymentMethod: '',
}
const emptyPaymentForm = {
  customerid: '',
  payment_date: '',
  payment_amount: '',
  payment_mode: 'Cash',
  order_type: '',
  payment_receipt: '',
  comment: '',
}

const getValue = (record, keys, fallback = '-') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback
const getOptionValue = (option) =>
  typeof option === 'object'
    ? getValue(option, ['id', 'value', 'customerid', 'customer_id', 'userid'])
    : option
const getOptionLabel = (option) =>
  typeof option === 'object'
    ? getValue(option, [
        'name',
        'label',
        'cust_name',
        'customer_name',
        'sales_exec_name',
        'sales_executive',
        'customertype',
        'type',
      ])
    : option
const formatDate = (value) => {
  if (!value || value === '-') return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
}
const toDateInputValue = (value) => {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10)
}
const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    maximumFractionDigits: 2,
  }).format(Number(value) || 0)
const getPaymentId = (payment) =>
  getValue(payment, ['paymentid', 'payment_id', 'id'])
const getPaymentDate = (payment) =>
  getValue(payment, ['payment_date', 'pay_date', 'date'])
const getPaymentAmount = (payment) =>
  getValue(payment, ['payment_amount', 'amount'], 0)
const getPaymentMode = (payment) => getValue(payment, ['payment_mode', 'mode'])
const getPaymentOrderType = (payment) => {
  const type = getValue(payment, ['order_type', 'orderType'], '')
  const match = orderTypes.find((option) => option.value === type)
  return match ? match.label : type
}

function OldPayment() {
  const { user, hasPermission } = useAuth()
  const canAddOldPayment = hasPermission('addOldPayment')
  const canEditOldPayment = hasPermission('editOldPayment')
  const canDeleteOldPayment = hasPermission('deleteOldPayment')
  const loginUserId = user?.userid ?? user?.user_id ?? user?.id ?? ''
  const [filters, setFilters] = useState(emptyFilters)
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters)
  const [isQueryOpen, setIsQueryOpen] = useState(false)
  const [payments, setPayments] = useState([])
  const [salesExecutives, setSalesExecutives] = useState([])
  const [customerTypes, setCustomerTypes] = useState([])
  const [customers, setCustomers] = useState([])
  const [nextCursor, setNextCursor] = useState(null)
  const [nextPage, setNextPage] = useState(null)
  const [currentCursor, setCurrentCursor] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [cursorHistory, setCursorHistory] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [paymentModal, setPaymentModal] = useState(null)
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm)
  const [isSavingPayment, setIsSavingPayment] = useState(false)
  const [paymentFormError, setPaymentFormError] = useState('')
  const [pendingDeletePayment, setPendingDeletePayment] = useState(null)
  const [isDeletingPayment, setIsDeletingPayment] = useState(false)

  const loadPayments = async (
    cursor = null,
    page = 1,
    query = appliedFilters,
  ) => {
    setIsLoading(true)
    setError('')
    try {
      const result = await getOldPayments({ cursor, page, ...query })
      setPayments(result.payments)
      setNextCursor(result.nextCursor)
      setNextPage(result.nextPage)
      setCurrentCursor(cursor)
      setCurrentPage(result.currentPage || page)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    Promise.all([getSalesExecutives(), getCustomerTypes(), getAllCustomers()])
      .then(([loadedSalesExecutives, loadedCustomerTypes, loadedCustomers]) => {
        setSalesExecutives(loadedSalesExecutives)
        setCustomerTypes(loadedCustomerTypes)
        setCustomers(loadedCustomers)
      })
      .catch((requestError) => setError(requestError.message))
    loadPayments(null, 1, emptyFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateFilter = (event) => {
    const { name, value } = event.target
    setFilters((current) => ({
      ...current,
      [name]: value,
      ...(name === 'customerType' ? { customer: '' } : {}),
    }))
  }
  const handleCustomerTypeChange = async (event) => {
    const customerType = event.target.value
    setFilters((current) => ({
      ...current,
      customerType,
      customer: '',
    }))
    setError('')

    try {
      setCustomers(await getAllCustomers(customerType))
    } catch (requestError) {
      setCustomers([])
      setError(requestError.message)
    }
  }
  const searchPayments = (event) => {
    event.preventDefault()
    setAppliedFilters(filters)
    setCursorHistory([])
    loadPayments(null, 1, filters)
  }
  const clearFilters = () => {
    setFilters(emptyFilters)
    setAppliedFilters(emptyFilters)
    setCursorHistory([])
    loadPayments(null, 1, emptyFilters)
  }
  const goToNextPage = () => {
    if (!nextCursor && !nextPage) return
    setCursorHistory((history) => [
      ...history,
      { cursor: currentCursor, page: currentPage },
    ])
    loadPayments(nextCursor, nextPage || currentPage, filters)
  }
  const goToPreviousPage = () => {
    if (!cursorHistory.length) return
    const history = [...cursorHistory]
    const previous = history.pop()
    setCursorHistory(history)
    loadPayments(previous.cursor, previous.page, filters)
  }

  const openAddPaymentModal = () => {
    setPaymentFormError('')
    setPaymentForm({
      ...emptyPaymentForm,
      payment_date: new Date().toISOString().slice(0, 10),
    })
    setPaymentModal({ mode: 'add' })
  }
  const openEditPaymentModal = (payment) => {
    setPaymentFormError('')
    setPaymentForm({
      customerid: String(getValue(payment, ['customerid'], '')),
      payment_date: toDateInputValue(getPaymentDate(payment)),
      payment_amount: String(getPaymentAmount(payment) ?? ''),
      payment_mode: getPaymentMode(payment) || 'Cash',
      order_type: getValue(payment, ['order_type', 'orderType'], ''),
      payment_receipt: getValue(payment, ['payment_receipt', 'receipt'], ''),
      comment: getValue(payment, ['comment'], ''),
    })
    setPaymentModal({ mode: 'edit', payment })
  }
  const closePaymentModal = () => {
    if (isSavingPayment) return
    setPaymentModal(null)
  }
  const updatePaymentFormField = (event) => {
    const { name, value } = event.target
    setPaymentForm((current) => ({ ...current, [name]: value }))
  }
  const submitPaymentForm = async (event) => {
    event.preventDefault()
    setIsSavingPayment(true)
    setPaymentFormError('')
    try {
      const payload = {
        customerid: paymentForm.customerid,
        payment_date: paymentForm.payment_date,
        payment_amount: paymentForm.payment_amount,
        payment_mode: paymentForm.payment_mode,
        order_type: paymentForm.order_type,
        payment_receipt: paymentForm.payment_receipt,
        comment: paymentForm.comment,
        received_user: loginUserId,
      }
      if (paymentModal?.mode === 'edit') {
        await updateOldPayment(getPaymentId(paymentModal.payment), payload)
      } else {
        await createOldPayment(payload)
      }
      setPaymentModal(null)
      await loadPayments(currentCursor, currentPage, appliedFilters)
    } catch (requestError) {
      setPaymentFormError(requestError.message)
    } finally {
      setIsSavingPayment(false)
    }
  }

  const confirmDeletePayment = async () => {
    if (!pendingDeletePayment) return
    setIsDeletingPayment(true)
    setError('')
    try {
      await deleteOldPayment(getPaymentId(pendingDeletePayment))
      setPendingDeletePayment(null)
      await loadPayments(currentCursor, currentPage, appliedFilters)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsDeletingPayment(false)
    }
  }

  return (
    <div className='old-payments-page'>
      <nav className='old-payments-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <strong>Old Payments</strong>
      </nav>
      <header className='old-payments-page__header'>
        <div>
          <span className='app-content__eyebrow'>Sales</span>
          <h1>Old Payments</h1>
          <p>
            Review legacy payment records and narrow the list by date, value,
            and account.
          </p>
        </div>
        {canAddOldPayment && (
          <button
            className='old-payments-button old-payments-button--primary'
            type='button'
            onClick={openAddPaymentModal}
          >
            + Add payment
          </button>
        )}
      </header>
      {error && (
        <div className='old-payments-page__error' role='alert'>
          {error}
        </div>
      )}

      <section
        className='old-payments-query-card'
        aria-labelledby='old-payments-query-title'
      >
        <button
          className='old-payments-query-card__toggle'
          type='button'
          onClick={() => setIsQueryOpen((open) => !open)}
          aria-expanded={isQueryOpen}
        >
          <span>
            <span className='app-content__eyebrow'>Refine results</span>
            <strong id='old-payments-query-title'>Payment query</strong>
          </span>
          <span aria-hidden='true'>{isQueryOpen ? '−' : '+'}</span>
        </button>
        {isQueryOpen && (
          <form className='old-payments-query-form' onSubmit={searchPayments}>
            <fieldset className='old-payments-query-form__range'>
              <legend>Date range</legend>
              <label>
                From date
                <input
                  name='fromDate'
                  type='date'
                  value={filters.fromDate}
                  onChange={updateFilter}
                />
              </label>
              <label>
                To date
                <input
                  name='toDate'
                  type='date'
                  value={filters.toDate}
                  onChange={updateFilter}
                />
              </label>
            </fieldset>
            <fieldset className='old-payments-query-form__range'>
              <legend>Amount range</legend>
              <label>
                From amount
                <input
                  name='fromAmount'
                  type='number'
                  min='0'
                  step='0.01'
                  value={filters.fromAmount}
                  onChange={updateFilter}
                />
              </label>
              <label>
                To amount
                <input
                  name='toAmount'
                  type='number'
                  min='0'
                  step='0.01'
                  value={filters.toAmount}
                  onChange={updateFilter}
                />
              </label>
            </fieldset>
            <label>
              Customer type
              <select
                name='customerType'
                value={filters.customerType}
                onChange={handleCustomerTypeChange}
              >
                <option value=''>All customer types</option>
                {customerTypes.map((option) => (
                  <option
                    key={getOptionValue(option)}
                    value={getOptionValue(option)}
                  >
                    {getOptionLabel(option)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Customer
              <select
                name='customer'
                value={filters.customer}
                onChange={updateFilter}
              >
                <option value=''>All customers</option>
                {customers.map((customer) => (
                  <option
                    key={getValue(customer, ['customerid', 'id'])}
                    value={getValue(customer, ['customerid', 'id'])}
                  >
                    {getValue(customer, ['cust_name', 'name', 'company'])}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Order type
              <select
                name='orderType'
                value={filters.orderType}
                onChange={updateFilter}
              >
                <option value=''>All order types</option>
                {orderTypes.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Sales executive
              <select
                name='salesExecutive'
                value={filters.salesExecutive}
                onChange={updateFilter}
              >
                <option value=''>All sales executives</option>
                {salesExecutives.map((option) => (
                  <option
                    key={getOptionValue(option)}
                    value={getOptionValue(option)}
                  >
                    {getOptionLabel(option)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Payment method
              <select
                name='paymentMethod'
                value={filters.paymentMethod}
                onChange={updateFilter}
              >
                <option value=''>All payment methods</option>
                {paymentMethods.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <div className='old-payments-query-form__actions'>
              <button
                className='old-payments-button old-payments-button--primary'
                type='submit'
              >
                Search
              </button>
              <button
                className='old-payments-button'
                type='button'
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>
          </form>
        )}
      </section>

      <section
        className='old-payments-table-card'
        aria-labelledby='old-payments-table-title'
      >
        <div className='old-payments-table-card__header'>
          <div>
            <span className='app-content__eyebrow'>Payment register</span>
            <h2 id='old-payments-table-title'>Old payment list</h2>
            <p>{payments.length} payments on this page</p>
          </div>
        </div>
        <div className='old-payments-table-wrap'>
          <table className='old-payments-table'>
            <thead>
              <tr>
                <th>Payment ID</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Order type</th>
                <th>Received by</th>
                <th>Method</th>
                <th>Amount</th>
                <th>
                  <span className='sr-only'>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className='old-payments-table__empty' colSpan='8'>
                    Loading payments...
                  </td>
                </tr>
              ) : payments.length ? (
                payments.map((payment, index) => (
                  <tr key={`${getPaymentId(payment)}-${index}`}>
                    <td>
                      <strong>{getPaymentId(payment)}</strong>
                    </td>
                    <td>{formatDate(getPaymentDate(payment))}</td>
                    <td>
                      {getValue(payment, [
                        'cust_name',
                        'customer_name',
                        'customer',
                      ])}
                    </td>
                    <td>{getPaymentOrderType(payment)}</td>
                    <td>
                      {getValue(payment, ['received_user', 'received_by_user'])}
                    </td>
                    <td>
                      <span className='old-payments-status'>
                        {getPaymentMode(payment)}
                      </span>
                    </td>
                    <td className='old-payments-table__amount'>
                      {formatCurrency(getPaymentAmount(payment))}
                    </td>
                    <td>
                      <div className='old-payments-actions'>
                        {canEditOldPayment && (
                          <button
                            type='button'
                            onClick={() => openEditPaymentModal(payment)}
                          >
                            Edit
                          </button>
                        )}
                        {canDeleteOldPayment && (
                          <button
                            className='old-payments-actions__delete'
                            type='button'
                            onClick={() => setPendingDeletePayment(payment)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className='old-payments-table__empty' colSpan='8'>
                    No payments match the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className='old-payments-pagination'>
          <span>Showing up to 20 payments</span>
          <div>
            <button
              type='button'
              onClick={goToPreviousPage}
              disabled={!cursorHistory.length || isLoading}
            >
              Previous
            </button>
            <button
              type='button'
              onClick={goToNextPage}
              disabled={(!nextCursor && !nextPage) || isLoading}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {paymentModal && (
        <div
          className='old-payments-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget && closePaymentModal()
          }
        >
          <section
            className='old-payments-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='payment-form-title'
          >
            <div className='old-payments-modal__header'>
              <div>
                <span className='app-content__eyebrow'>
                  {paymentModal.mode === 'edit'
                    ? 'Edit payment'
                    : 'Add payment'}
                </span>
                <h2 id='payment-form-title'>
                  {paymentModal.mode === 'edit'
                    ? `Payment ${getPaymentId(paymentModal.payment)}`
                    : 'New payment'}
                </h2>
              </div>
              <button
                type='button'
                onClick={closePaymentModal}
                aria-label='Close payment form'
                disabled={isSavingPayment}
              >
                x
              </button>
            </div>
            {paymentFormError && (
              <div className='old-payments-page__error' role='alert'>
                {paymentFormError}
              </div>
            )}
            <form className='old-payments-form' onSubmit={submitPaymentForm}>
              <label>
                Customer
                <select
                  name='customerid'
                  value={paymentForm.customerid}
                  onChange={updatePaymentFormField}
                  required
                >
                  <option value=''>Select customer</option>
                  {customers.map((customer) => (
                    <option
                      key={getValue(customer, ['customerid', 'id'])}
                      value={getValue(customer, ['customerid', 'id'])}
                    >
                      {getValue(customer, ['cust_name', 'name', 'company'])}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Payment date
                <input
                  name='payment_date'
                  type='date'
                  value={paymentForm.payment_date}
                  onChange={updatePaymentFormField}
                  required
                />
              </label>
              <label>
                Amount
                <input
                  name='payment_amount'
                  type='number'
                  min='0'
                  step='0.01'
                  value={paymentForm.payment_amount}
                  onChange={updatePaymentFormField}
                  required
                />
              </label>
              <label>
                Payment method
                <select
                  name='payment_mode'
                  value={paymentForm.payment_mode}
                  onChange={updatePaymentFormField}
                >
                  {paymentMethods.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Order type
                <select
                  name='order_type'
                  value={paymentForm.order_type}
                  onChange={updatePaymentFormField}
                >
                  <option value=''>Select order type</option>
                  {orderTypes.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Receipt number
                <input
                  name='payment_receipt'
                  type='text'
                  value={paymentForm.payment_receipt}
                  onChange={updatePaymentFormField}
                />
              </label>
              <label className='old-payments-form__full'>
                Comment
                <textarea
                  name='comment'
                  rows='3'
                  value={paymentForm.comment}
                  onChange={updatePaymentFormField}
                />
              </label>
              <div className='old-payments-form__actions'>
                <button
                  className='old-payments-button'
                  type='button'
                  onClick={closePaymentModal}
                  disabled={isSavingPayment}
                >
                  Cancel
                </button>
                <button
                  className='old-payments-button old-payments-button--primary'
                  type='submit'
                  disabled={isSavingPayment}
                >
                  {isSavingPayment ? 'Saving...' : 'Save payment'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {pendingDeletePayment && (
        <div
          className='old-payments-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            !isDeletingPayment &&
            setPendingDeletePayment(null)
          }
        >
          <section
            className='old-payments-modal'
            role='alertdialog'
            aria-modal='true'
            aria-labelledby='delete-payment-title'
            aria-describedby='delete-payment-description'
          >
            <div className='old-payments-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Delete payment</span>
                <h2 id='delete-payment-title'>Delete old payment?</h2>
              </div>
              <button
                type='button'
                onClick={() => setPendingDeletePayment(null)}
                aria-label='Close delete confirmation'
                disabled={isDeletingPayment}
              >
                x
              </button>
            </div>
            <p
              className='old-payments-modal__description'
              id='delete-payment-description'
            >
              Payment {getPaymentId(pendingDeletePayment)} will be permanently
              deleted. This action cannot be undone.
            </p>
            <div className='old-payments-modal__actions'>
              <button
                type='button'
                onClick={() => setPendingDeletePayment(null)}
                disabled={isDeletingPayment}
              >
                Cancel
              </button>
              <button
                className='old-payments-modal__delete-button'
                type='button'
                onClick={confirmDeletePayment}
                disabled={isDeletingPayment}
              >
                {isDeletingPayment ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

export default OldPayment
